/**
 * Layout-Context — exposed Shell-State (User, Mobile-Drawer, Upload-Modal) für
 * Pages/Topbar/Sidebar ohne Prop-Drilling.
 *
 * Eingeführt in Slice-1 Redesign (2026-05-08).
 *
 * Pattern:
 *  - Provider+Hook split (lebt hier, Provider in `components/Layout.tsx`) —
 *    `react-refresh/only-export-components` Convention.
 *  - NOOP_LAYOUT_API Method-Mirror: wenn Hook außerhalb Provider gerufen
 *    wird (LoginPage, isolierte Tests), returnt no-op statt zu throwen.
 *    Im Dev-Mode einmaliges `console.warn` damit silent-structural-Provider-
 *    Bugs sichtbar werden. Pattern aus
 *    `patterns/react.md § Context-Provider No-Op-Fallback`.
 */
import { createContext, useContext, type MutableRefObject, type RefObject } from 'react';

/** Subset des Auth-User-Models das die Shell-UI braucht (Greeting, Avatar,
 *  Initials, ProfilePage). Voller Type lebt in App.tsx als `Me`.
 *  Email wurde in PR #27 für ProfilePage ergänzt (read-only Display). */
export type LayoutUser = {
  email: string;
  displayName: string;
  role: string;
};

export type LayoutApi = {
  /** Aktuell-eingeloggter User für Pages die das ohne Prop-Drilling
   *  brauchen (DashboardPage Greeting). `null` wenn Hook außerhalb
   *  Layout-Provider (LoginPage, Tests). Slice-2 (2026-05-09). */
  user: LayoutUser | null;
  /** Mobile-Drawer-Open-State. Lebt im LayoutContext (NICHT eigener
   *  DrawerContext) damit 1 Provider den ganzen Shell-State exposed
   *  (user + drawer + upload-modal). Slice-3 (2026-05-09).
   *
   *  Hamburger ist OPEN-ONLY — close paths sind Backdrop-click,
   *  Esc-key, Auto-close on nav. setSidebarOpen bekommt boolean
   *  (kein Toggle-API), weil Caller immer wissen was sie wollen
   *  (open=true vom Hamburger, open=false von close-paths). */
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  /** Desktop-Collapse-State (schmale Icon-Rail ↔ volle Nav mit Labels).
   *  Getrennt von `sidebarOpen` (das ist der Mobile-Drawer): auf Desktop ist
   *  die Sidebar immer sichtbar, `sidebarCollapsed` steuert nur ihre Breite.
   *  localStorage-persistiert (`lib/sidebar-collapsed.ts`). 2026-07-19. */
  sidebarCollapsed: boolean;
  toggleSidebarCollapsed: () => void;
  /** Refs für Drawer-Focus-Management (Slice 3). Topbar registriert
   *  hamburgerRef hier für focus-restore-on-close, Sidebar registriert
   *  firstNavLinkRef für autofocus-on-open. */
  hamburgerRef: MutableRefObject<HTMLButtonElement | null>;
  firstNavLinkRef: MutableRefObject<HTMLAnchorElement | null>;
  // Der Upload-Flow lebt seit 2026-06-16 NICHT mehr im LayoutContext, sondern
  // im eigenen <UploadProvider> (`lib/upload-context.tsx` → `useUploadModal`).
  // Grund: der Upload-State ändert pro Progress-Tick — im LayoutContext hätte das
  // app-weite Re-Renders ausgelöst (Session-23-Lehre). Siehe `patterns/react.md`.
};

const NOOP_LAYOUT_API: LayoutApi = {
  user: null,
  sidebarOpen: false,
  setSidebarOpen: () => {
    /* no-op — Hook außerhalb LayoutProvider */
  },
  sidebarCollapsed: false,
  toggleSidebarCollapsed: () => {
    /* no-op — Hook außerhalb LayoutProvider */
  },
  // Stable no-op refs — never read in NOOP-mode by design.
  hamburgerRef: { current: null },
  firstNavLinkRef: { current: null },
};

export const LayoutContext = createContext<LayoutApi | null>(null);

let warnedOnce = false;

function useLayoutApi(): LayoutApi {
  const ctx = useContext(LayoutContext);
  if (ctx) return ctx;
  if (import.meta.env.DEV && !warnedOnce) {
    warnedOnce = true;
    console.warn(
      '[layout-context] Hook used outside <LayoutProvider> — returning no-op. ' +
        'If this is in a page rendered by App.tsx authed-branch, the Provider ordering is broken.',
    );
  }
  return NOOP_LAYOUT_API;
}

/** Read-only accessor für eingeloggten User. Returns `null` wenn kein
 *  Layout-Provider im Tree (LoginPage, Tests) — Caller muss null-handling
 *  selbst machen. Slice-2 (2026-05-09). */
export function useLayoutUser(): LayoutUser | null {
  return useLayoutApi().user;
}

// `useUploadModal` lebt seit 2026-06-16 in `lib/upload-context.tsx` (Upload-Flow
// in den eigenen UploadProvider gehoben). Consumer importieren von dort.

// ── Scroll-Container-Ref (Library Infinite-Scroll + Virtualisierung) ──────
// Der App-Scroll-Parent ist <main> (overflow-auto, h-screen), NICHT `window`.
// Der Infinite-Fetch-Hook (Prefetch beim Ende der geladenen Range) und
// @tanstack/react-virtual brauchen GENAU dieses Element als getScrollElement /
// IntersectionObserver-root. Eigener schlanker Context statt Aufblähen der
// LayoutApi. Layout.tsx hängt die <main>-Ref hier ein.
const FALLBACK_SCROLL_REF: RefObject<HTMLElement | null> = { current: null };

export const ScrollContainerContext = createContext<RefObject<HTMLElement | null> | null>(null);

/** Ref auf den App-Scroll-Container (<main>). Außerhalb des Layout-Providers
 *  (LoginPage, isolierte Tests) → stabile Ref auf `null`; der Virtualizer misst
 *  dann nichts (Height-Mock-Seam in jsdom-Tests). */
export function useScrollContainerRef(): RefObject<HTMLElement | null> {
  return useContext(ScrollContainerContext) ?? FALLBACK_SCROLL_REF;
}

/** Read+Write accessor für Mobile-Drawer-State. Slice-3 (2026-05-09).
 *  Hamburger ist open-only: setSidebarOpen(true) öffnet, close paths
 *  sind Backdrop/Esc/Auto-close. */
export function useSidebarState(): {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  /** Desktop-Icon-Rail-Modus (≥md). Mobile-Drawer ignoriert das. */
  collapsed: boolean;
  toggleCollapsed: () => void;
  hamburgerRef: MutableRefObject<HTMLButtonElement | null>;
  firstNavLinkRef: MutableRefObject<HTMLAnchorElement | null>;
} {
  const api = useLayoutApi();
  return {
    sidebarOpen: api.sidebarOpen,
    setSidebarOpen: api.setSidebarOpen,
    collapsed: api.sidebarCollapsed,
    toggleCollapsed: api.toggleSidebarCollapsed,
    hamburgerRef: api.hamburgerRef,
    firstNavLinkRef: api.firstNavLinkRef,
  };
}
